import config from 'constants/config'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { getCurrentUserId } from 'selectors/accountSelectors'

const TRIP_CHAT_MOBILE_ALLOWED_USER_IDS: ReadonlySet<string> = new Set([
  'a2f8436d-5de0-4475-ba3b-9f7586095646',
  '309bbbeb-24c1-4929-becd-4e5e67c2b1d9',
  '5f34d7cb-3945-44f5-a2de-d3dcc828e825',
  'd638c4cb-6a26-4088-9c1c-79897d2fbda0',
  'cf01cdf4-1019-4641-bf97-843fcd8fae15',
  '711f4d43-9387-4883-a6e2-8764f556e930',
])

// Temporary TRP-4081 rollout cohort for production/test mobile chat access.
// Remove once the mobile rollout moves to an Optimizely audience or opens beyond this cohort.
export function canUseTripChatMobile({
  appEnv,
  currentUserId,
  isTripChatMobileEnabled,
}: {
  appEnv: App.AppConfig['APP_ENV']
  currentUserId?: string
  isTripChatMobileEnabled: boolean
}): boolean {
  if (!isTripChatMobileEnabled) {
    return false
  }

  if (appEnv !== 'production' && appEnv !== 'test') {
    return true
  }

  return (
    !!currentUserId &&
    TRIP_CHAT_MOBILE_ALLOWED_USER_IDS.has(currentUserId)
  )
}

export function useCanAccessMobileChat(): boolean {
  const isTripChatMobileEnabled = useFeatureFlag(OptimizelyFeatureFlags.tripChatMobile)
  const currentUserId = useAppSelector(getCurrentUserId)

  if (!config.ENABLE_TRIP_PLANNER) {
    return false
  }

  return canUseTripChatMobile({
    appEnv: config.APP_ENV,
    currentUserId,
    isTripChatMobileEnabled,
  })
}

/**
 * On LE desktop we always route users to the chat trip planner. Mobile/tablet
 * chat access is gated by the mobile chat flag, with production/test access
 * further limited to the temporary rollout cohort. Brands without trip planner
 * get `false` so consumers hide any trip-planning UI entirely.
 */
export default function useShouldUseChat(): boolean {
  const isMobileOrTablet = useIsMobileOrTablet()
  const canAccessMobileChat = useCanAccessMobileChat()

  if (!config.ENABLE_TRIP_PLANNER) {
    return false
  }

  if (isMobileOrTablet) {
    return canAccessMobileChat
  }

  return true
}
